import { FiltersDefinition, FiltersState } from '@factorialco/f0-react/dist/experimental'
import { AnalyticsSnapshotResultsQuery } from 'generated/resources/graphql'
import { z } from 'zod'

import * as JsonLogic from 'modules/ai_reports/components/DatasourceTypeRenderer/jsonLogic'

type Schema =
  AnalyticsSnapshotResultsQuery['iris']['snapshotResultsConnection']['nodes'][number]['schema'][number]

const INVALID_STRUCTURE = 'INVALID_STRUCTURE'

function filterForType(column: Schema): FiltersDefinition<string> {
  const label = column.displayName ?? column.name
  const relevantType = column.items ?? column.dataType

  if (column.enumValues.length > 0) {
    return {
      [column.name]: {
        type: 'in',
        label,
        options: {
          options: column.enumValues,
          cache: true,
        },
      },
    }
  }

  if (relevantType === 'string' && column.formatHint === 'date') {
    return { [column.name]: { type: 'date', options: { mode: 'range' }, label } }
  }

  if (relevantType === 'string') {
    return { [column.name]: { type: 'search', label } }
  }

  if (relevantType === 'number') {
    return { [column.name]: { type: 'number', label, options: { openCloseToggle: true } } }
  }

  return {}
}

export function filterDefinitionsFromSchema(schema: Schema[]): FiltersDefinition<string> {
  return schema.map(filterForType).reduce((acc, curr) => ({ ...acc, ...curr }), {})
}

const numberFilterSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('range'),
    from: z.object({
      value: z.number().optional(),
      closed: z.boolean(),
    }),
    to: z.object({
      value: z.number().optional(),
      closed: z.boolean(),
    }),
  }),
  z.object({
    mode: z.literal('single'),
    value: z.number().optional(),
  }),
])

type FilterResult = {
  fieldName: string
  value: FiltersState<FiltersDefinition<string>>[string]
}

type ParseErrorType = 'INVALID_STRUCTURE'

type ParseError = {
  type: ParseErrorType
  message: string
  context?: Record<string, unknown>
}

type ParseResult = { success: true; filter: FilterResult } | { success: false; error: ParseError }

export function datatableFiltersToJsonLogicFilters(
  filters: FiltersState<FiltersDefinition<string>>
): JsonLogic.FilterChain | undefined {
  const conditions = Object.entries(filters).flatMap((entry) => datatableFilterToJsonLogic(entry))

  if (conditions.length === 0) return undefined

  return { and: conditions }
}

function parseNumberFilter(
  field: JsonLogic.VarExpression,
  numberFilter: z.infer<typeof numberFilterSchema>
): JsonLogic.Filter[] {
  if (numberFilter.mode === 'single') {
    if (numberFilter.value == null) return []
    return [{ '===': [field, numberFilter.value] }]
  }

  if (numberFilter.mode === 'range') {
    const startValue = numberFilter.from.value
    const endValue = numberFilter.to.value

    if (startValue == null && endValue == null) return []

    if (startValue != null && endValue != null) {
      if (startValue === endValue) return [{ '===': [field, startValue] }]

      if (numberFilter.from.closed && numberFilter.to.closed)
        return [{ '>=': [field, startValue] }, { '<=': [field, endValue] }]
      if (!numberFilter.from.closed && !numberFilter.to.closed)
        return [{ '>': [field, startValue] }, { '<': [field, endValue] }]
      if (numberFilter.from.closed && !numberFilter.to.closed)
        return [{ '>=': [field, startValue] }, { '<': [field, endValue] }]
      if (!numberFilter.from.closed && numberFilter.to.closed)
        return [{ '>': [field, startValue] }, { '<=': [field, endValue] }]

      return [{ between: [startValue, field, endValue] }]
    }

    if (startValue != null) {
      if (numberFilter.from.closed) return [{ '>=': [field, startValue] }]
      return [{ '>': [field, startValue] }]
    }

    if (endValue != null) {
      if (numberFilter.to.closed) return [{ '<=': [field, endValue] }]
      return [{ '<': [field, endValue] }]
    }
  }

  // Unknown number filter mode
  return []
}

function datatableFilterToJsonLogic([fieldName, filterValue]: [
  string,
  unknown
]): JsonLogic.Filter[] {
  // Skip undefined/null/empty values
  if (filterValue === undefined || filterValue === null || filterValue === '') return []
  const field = { var: fieldName }

  // Number filters
  const numberFilterResult = numberFilterSchema.safeParse(filterValue)
  if (numberFilterResult.success) {
    return parseNumberFilter(field, numberFilterResult.data)
  }

  // Check for DateRange object: { from: Date, to: Date }
  if (isDateRange(filterValue)) {
    return [
      {
        between: [filterValue.from?.toISOString(), field, filterValue.to.toISOString()],
      },
    ]
  }

  // Enum filters (arrays)
  if (Array.isArray(filterValue)) {
    if (filterValue.length === 0) return []
    if (filterValue.length === 1) return [{ '===': [field, filterValue[0]] }]
    if (filterValue.length > 1) return [{ in: [field, filterValue] }]
  }

  // Everything else: treat as string search
  if (typeof filterValue === 'string') {
    return [{ in: [filterValue, field] }]
  }

  return []
}

// ============================================================================
// Type Guards
// ============================================================================

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const isSuccessResult = (result: ParseResult): result is ParseResult & { success: true } => {
  return result.success
}

const isDateRange = (value: unknown): value is { from: Date; to: Date } => {
  return (
    isObject(value) &&
    'from' in value &&
    'to' in value &&
    value.from instanceof Date &&
    value.to instanceof Date
  )
}

const extractAndConditions = (
  parsed: unknown
): { success: true; data: unknown[] } | { success: false; error: string } => {
  if (!isObject(parsed) || !('and' in parsed) || !Array.isArray(parsed.and)) {
    return { success: false, error: 'Missing "and" array in JsonLogic structure' }
  }
  return { success: true, data: parsed.and }
}

// ============================================================================
// Pattern Matchers
// ============================================================================

const parseInExpressionEnum = (expression: JsonLogic.InEnumExpression): ParseResult => {
  const fieldName = expression.in[0].var
  const values = expression.in[1]

  const filteredValues = values.filter((v) => v !== '')
  if (filteredValues.length === 0) {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'No valid values provided' },
    }
  }

  return {
    success: true,
    filter: { fieldName, value: filteredValues },
  }
}

const parseInExpressionSearch = (expression: JsonLogic.InSearchExpression): ParseResult => {
  const searchValue = expression.in[0]
  const secondElement = expression.in[1]

  // Handle both direct VarExpression and array with single VarExpression
  const varExpr =
    Array.isArray(secondElement) && secondElement.length === 1 ? secondElement[0] : secondElement
  const fieldName = varExpr.var

  return {
    success: true,
    filter: { fieldName, value: searchValue },
  }
}

const parseBetweenNumberExpression = (
  expression: JsonLogic.BetweenNumberExpression
): ParseResult => {
  const [from, varExpr, to] = expression.between
  const fieldName = varExpr.var

  return {
    success: true,
    filter: {
      fieldName,
      value: {
        mode: 'range',
        from: { value: from, closed: true },
        to: { value: to, closed: true },
      },
    },
  }
}

const parseBetweenDateExpression = (expression: JsonLogic.BetweenDateExpression): ParseResult => {
  const [from, varExpr, to] = expression.between
  const fieldName = varExpr.var

  try {
    return {
      success: true,
      filter: {
        fieldName,
        value: {
          from: new Date(from),
          to: new Date(to),
        },
      },
    }
  } catch (error) {
    return {
      success: false,
      error: {
        type: 'INVALID_STRUCTURE',
        message: `Error parsing dates for column ${fieldName}`,
        context: { fieldName, from, to, error: String(error) },
      },
    }
  }
}

const parseLessThanExpression = (expression: JsonLogic.LessThanExpression): ParseResult => {
  const [first, second] = expression['<']

  // z < 10
  if (typeof first !== 'number' && typeof second === 'number')
    return {
      success: true,
      filter: {
        fieldName: first.var,
        value: {
          mode: 'range',
          from: { value: undefined, closed: true },
          to: { value: second, closed: false },
        },
      },
    }

  // 10 < z (can be rewritten as z >= 10)
  if (typeof first === 'number' && typeof second !== 'number')
    return {
      success: true,
      filter: {
        fieldName: second.var,
        value: {
          mode: 'range',
          from: { value: first, closed: true },
          to: { value: undefined, closed: true },
        },
      },
    }

  return {
    success: false,
    error: { type: INVALID_STRUCTURE, message: 'Exactly one operand must be a variable' },
  }
}

const parseGreaterThanExpression = (expression: JsonLogic.GreaterThanExpression): ParseResult => {
  const [first, second] = expression['>']

  // z > 10
  if (typeof first !== 'number' && typeof second === 'number')
    return {
      success: true,
      filter: {
        fieldName: first.var,
        value: {
          mode: 'range',
          from: { value: second, closed: false },
          to: { value: undefined, closed: true },
        },
      },
    }

  // 10 > z (can be rewritten as z <= 10)
  if (typeof first === 'number' && typeof second !== 'number')
    return {
      success: true,
      filter: {
        fieldName: second.var,
        value: {
          mode: 'range',
          from: { value: undefined, closed: true },
          to: { value: first, closed: true },
        },
      },
    }

  return {
    success: false,
    error: { type: INVALID_STRUCTURE, message: 'Exactly one operand must be a variable' },
  }
}

const parseGreaterThanOrEqualExpression = (
  expression: JsonLogic.GreaterThanOrEqualExpression
): ParseResult => {
  const [first, second] = expression['>=']

  // z >= 10
  if (typeof first !== 'number' && typeof second === 'number')
    return {
      success: true,
      filter: {
        fieldName: first.var,
        value: {
          mode: 'range',
          from: { value: second, closed: true },
          to: { value: undefined, closed: true },
        },
      },
    }

  // 10 >= z (can be rewritten as z < 10)
  if (typeof first === 'number' && typeof second !== 'number')
    return {
      success: true,
      filter: {
        fieldName: second.var,
        value: {
          mode: 'range',
          from: { value: undefined, closed: true },
          to: { value: first, closed: false },
        },
      },
    }

  return {
    success: false,
    error: { type: INVALID_STRUCTURE, message: 'Exactly one operand must be a variable' },
  }
}

const parseLessThanOrEqualExpression = (
  expression: JsonLogic.LessThanOrEqualExpression
): ParseResult => {
  const [first, second] = expression['<=']

  if (typeof first !== 'number' && typeof second === 'number')
    // z <= 10
    return {
      success: true,
      filter: {
        fieldName: first.var,
        value: {
          mode: 'range',
          from: { value: undefined, closed: true },
          to: { value: second, closed: true },
        },
      },
    }

  if (typeof first === 'number' && typeof second !== 'number')
    // 10 <= z (can be rewritten as z > 10)
    return {
      success: true,
      filter: {
        fieldName: second.var,
        value: {
          mode: 'range',
          from: { value: undefined, closed: true },
          to: { value: first, closed: false },
        },
      },
    }

  return {
    success: false,
    error: { type: INVALID_STRUCTURE, message: 'Exactly one operand must be a variable' },
  }
}

const parseEqualityNumberExpression = (
  expression: JsonLogic.EqualityNumberExpression | JsonLogic.StrictEqualityNumberExpression
): ParseResult => {
  const [first, second] = '==' in expression ? expression['=='] : expression['===']

  // z == 10
  if (typeof first !== 'number' && typeof second === 'number')
    return {
      success: true,
      filter: { fieldName: first.var, value: { mode: 'single', value: second } },
    }

  // 10 == z
  if (typeof first === 'number' && typeof second !== 'number')
    return {
      success: true,
      filter: { fieldName: second.var, value: { mode: 'single', value: first } },
    }

  return {
    success: false,
    error: { type: INVALID_STRUCTURE, message: 'Exactly one operand must be a variable' },
  }
}

const parseEqualityStringExpression = (
  expression: JsonLogic.EqualityStringExpression | JsonLogic.StrictEqualityStringExpression
): ParseResult => {
  const [first, second] = '==' in expression ? expression['=='] : expression['===']

  if (typeof first === 'string' && typeof second !== 'string')
    return {
      success: true,
      filter: { fieldName: second.var, value: [first] },
    }

  if (typeof first !== 'string' && typeof second === 'string')
    return {
      success: true,
      filter: { fieldName: first.var, value: [second] },
    }

  return {
    success: false,
    error: { type: INVALID_STRUCTURE, message: 'Exactly one operand must be a variable' },
  }
}

const jsonLogicToDataCollectionFilter = (condition: unknown): ParseResult => {
  const inExpressionResult = JsonLogic.jsonLogicInEnumSchema.safeParse(condition)
  if (inExpressionResult.success) {
    return parseInExpressionEnum(inExpressionResult.data)
  }

  const inExpressionSearchResult = JsonLogic.jsonLogicInSearchSchema.safeParse(condition)
  if (inExpressionSearchResult.success) {
    return parseInExpressionSearch(inExpressionSearchResult.data)
  }

  const lessThanExpressionResult = JsonLogic.jsonLogicLessThanSchema.safeParse(condition)
  if (lessThanExpressionResult.success) {
    return parseLessThanExpression(lessThanExpressionResult.data)
  }

  const greaterThanExpressionResult = JsonLogic.jsonLogicGreaterThanSchema.safeParse(condition)
  if (greaterThanExpressionResult.success) {
    return parseGreaterThanExpression(greaterThanExpressionResult.data)
  }

  const greaterThanOrEqualExpressionResult =
    JsonLogic.jsonLogicGreaterThanOrEqualSchema.safeParse(condition)
  if (greaterThanOrEqualExpressionResult.success) {
    return parseGreaterThanOrEqualExpression(greaterThanOrEqualExpressionResult.data)
  }

  const lessThanOrEqualExpressionResult =
    JsonLogic.jsonLogicLessThanOrEqualSchema.safeParse(condition)
  if (lessThanOrEqualExpressionResult.success) {
    return parseLessThanOrEqualExpression(lessThanOrEqualExpressionResult.data)
  }

  const equalityExpressionResult = JsonLogic.jsonLogicEqualityNumberSchema.safeParse(condition)
  if (equalityExpressionResult.success) {
    return parseEqualityNumberExpression(equalityExpressionResult.data)
  }

  const strictEqualityExpressionResult =
    JsonLogic.jsonLogicStrictEqualityNumberSchema.safeParse(condition)
  if (strictEqualityExpressionResult.success) {
    return parseEqualityNumberExpression(strictEqualityExpressionResult.data)
  }

  const equalityStringExpressionResult =
    JsonLogic.jsonLogicEqualityStringSchema.safeParse(condition)
  if (equalityStringExpressionResult.success) {
    return parseEqualityStringExpression(equalityStringExpressionResult.data)
  }

  const strictEqualityStringExpressionResult =
    JsonLogic.jsonLogicStrictEqualityStringSchema.safeParse(condition)
  if (strictEqualityStringExpressionResult.success) {
    return parseEqualityStringExpression(strictEqualityStringExpressionResult.data)
  }

  const betweenNumberExpressionResult = JsonLogic.jsonLogicBetweenNumberSchema.safeParse(condition)
  if (betweenNumberExpressionResult.success) {
    return parseBetweenNumberExpression(betweenNumberExpressionResult.data)
  }

  const betweenDateExpressionResult = JsonLogic.jsonLogicBetweenDateSchema.safeParse(condition)
  if (betweenDateExpressionResult.success) {
    return parseBetweenDateExpression(betweenDateExpressionResult.data)
  }

  return {
    success: false,
    error: {
      type: INVALID_STRUCTURE,
      message: 'Unknown condition pattern',
      context: { condition },
    },
  }
}

function coalesceFilters(
  acc: FiltersState<FiltersDefinition<string>>,
  [fieldName, value]: readonly [string, FiltersState<FiltersDefinition<string>>[string]]
) {
  const previousValue = acc[fieldName]
  if (previousValue == null) return { ...acc, [fieldName]: value }
  const numberFilterResult = numberFilterSchema.safeParse(value)

  // JsonLogic number filter needs two filters to express a range with different
  // open/closed flags. To accomplish this, we need to locate any previous
  // number filter for the same field and coalesce them into a single range
  // filter.
  if (numberFilterResult.success) {
    const numberFilter = numberFilterResult.data
    const previousNumberFilter = numberFilterSchema.parse(previousValue)
    if (previousNumberFilter.mode === 'single') return { ...acc, [fieldName]: value }
    if (numberFilter.mode === 'single') return { ...acc, [fieldName]: value }

    const from = numberFilter.from.value ?? previousNumberFilter.from.value
    const to = numberFilter.to.value ?? previousNumberFilter.to.value
    const fromClosed =
      numberFilter.from.value != null ? numberFilter.from.closed : previousNumberFilter.from.closed
    const toClosed =
      numberFilter.to.value != null ? numberFilter.to.closed : previousNumberFilter.to.closed

    const coalesced = {
      mode: 'range' as const,
      from: { value: Number(from), closed: fromClosed },
      to: { value: Number(to), closed: toClosed },
    }

    return { ...acc, [fieldName]: coalesced }
  }

  return { ...acc, [fieldName]: value }
}

/**
 * Converts saved jsonLogic filters back to OneDataCollection FiltersState format.
 * This is the reverse of datatableFiltersToJsonLogicFilters.
 *
 * @param jsonLogicFilters - JSON string containing jsonLogic filter structure
 * @returns FiltersState object that can be used by OneDataCollection
 */
export function jsonLogicFiltersToFiltersState(
  jsonLogicFilters: string
): FiltersState<FiltersDefinition<string>> {
  if (Object.keys(jsonLogicFilters).length === 0) {
    return {}
  }

  const conditionsResult = extractAndConditions(JSON.parse(jsonLogicFilters))
  if (!conditionsResult.success) {
    throw new Error(`Unexpected jsonLogic structure:': ${conditionsResult.error}`)
  }

  const parseResults = conditionsResult.data
    .map((condition) => jsonLogicToDataCollectionFilter(condition))
    .filter(isSuccessResult)

  return parseResults
    .map((result) => [result.filter.fieldName, result.filter.value] as const)
    .reduce(coalesceFilters, {})
}
